import { CheckCircleIcon } from "vega-icons";

import { useViewCoinvestNdaDocument } from "@vega-api/generated/coinvest-ndas-upload/coinvest-ndas-upload";
import { type DocumentMetadata } from "@vega-api/generated/types/documentMetadata";
import { ThemedButton } from "@vega-core/components/Buttons/ThemedButton";
import { TruncateText } from "@vega-core/components/TruncateText/TruncateText";
import { m } from "@vega-core/paraglide/messages";
import { downloadUrlAsFile } from "@vega-core/utils/download.helpers";

export type NdaUploadInputProps = {
  document?: DocumentMetadata;
  onFileSelect: (event: React.ChangeEvent<HTMLInputElement>) => void;
  isPending: boolean;
  title: string;
  accept: string;
  inputId: string;
  dataTestId: string;
  inputRef: React.RefObject<HTMLInputElement | null>;
  readOnly?: boolean;
};

export const NdaUploadInput = ({
  document,
  onFileSelect,
  isPending,
  title,
  accept,
  inputId,
  dataTestId,
  inputRef,
  readOnly = false,
}: NdaUploadInputProps) => {
  const documentName = document?.nameOfDocument;
  const documentId = document?.documentId;
  const { data: presignedUrl, isLoading: isLoadingDownload } =
    useViewCoinvestNdaDocument(documentId!, {
      query: {
        enabled: !!documentId,
        select: (data) => data.data?.presignedUrl?.url,
      },
    });

  const getButtonText = () => {
    if (isPending) return m.common_pending();

    if (document) return m.common_update();

    return m.common_upload();
  };

  return (
    <div className="flex items-center justify-between p-4 bg-background/primary border border-border rounded-md">
      <div>
        <h3 className="body-base-semibold text-text/primary">{title}</h3>
        <p className="body-small-regular text-text/secondary">
          {m.coInvestments_ndaDocument()}
        </p>
      </div>
      {documentName && presignedUrl ? (
        <div className="flex items-center gap-1">
          <CheckCircleIcon className="size-4 text-text/pending" />
          <p className="body-small-regular text-text/success">
            <ThemedButton
              className="body-small-regular"
              dataTestId={`${dataTestId}-download-link`}
              disabled={isLoadingDownload}
              onClick={() => downloadUrlAsFile(presignedUrl, documentName)}
              variant="text"
            >
              <TruncateText
                limit={20}
                text={documentName}
                tooltip={documentName}
              />
            </ThemedButton>
          </p>
        </div>
      ) : null}

      {readOnly ? null : (
        <div className="flex items-center gap-2">
          <input
            accept={accept}
            className="hidden"
            disabled={isPending}
            id={inputId}
            onChange={onFileSelect}
            ref={inputRef}
            type="file"
          />
          <ThemedButton
            as="label"
            className="min-h-7 h-9 px-3"
            dataTestId={`${dataTestId}-button`}
            disabled={isPending}
            htmlFor={inputId}
            variant="secondary"
          >
            {getButtonText()}
          </ThemedButton>
        </div>
      )}
    </div>
  );
};
